import { useState, useCallback, useRef } from 'react';
import axiosInstance from '../api/axiosConfig';

interface UseEmailValidationOptions {
  endpoint: string;
  debounceMs?: number;
  excludeId?: string;
}

interface EmailValidationResult {
  error: string | null;
  isChecking: boolean;
  validateEmail: (email: string) => void;
  clearError: () => void;
}

/**
 * Hook pour valider un email en temps réel via API
 * Respecte CONTEXT.md : pas de useEffect pour data fetching
 * Utilise un callback debounced avec Promise
 */
export const useEmailValidation = ({
  endpoint,
  debounceMs = 500,
  excludeId,
}: UseEmailValidationOptions): EmailValidationResult => {
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const validateEmail = useCallback(
    (email: string) => {
      // Nettoyer le timer précédent
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Annuler la requête précédente
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Si email vide, pas de validation
      if (!email.trim()) {
        setError(null);
        setIsChecking(false);
        return;
      }

      // Validation format email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Format d\'email invalide');
        setIsChecking(false);
        return;
      }

      // Indiquer qu'on va vérifier
      setIsChecking(true);
      setError(null);

      // Debounce
      debounceTimerRef.current = setTimeout(async () => {
        try {
          abortControllerRef.current = new AbortController();

          const params: Record<string, string> = { email: email.toLowerCase() };
          if (excludeId) {
            params.excludeId = excludeId;
          }

          const { data } = await axiosInstance.get(endpoint, {
            params,
            signal: abortControllerRef.current.signal,
          });

          if (data.exists) {
            setError(data.message || 'Cet email est déjà utilisé');
          } else {
            setError(null);
          }
        } catch (err: any) {
          // Ignorer les erreurs d'annulation
          if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
            return;
          }

          console.error('Erreur validation email:', err);

          if (err.response?.status === 409 || err.response?.data?.message) {
            setError(err.response.data.message || 'Cet email est déjà utilisé');
          } else {
            // En cas d'erreur réseau, ne pas bloquer l'utilisateur
            setError(null);
          }
        } finally {
          setIsChecking(false);
        }
      }, debounceMs);
    },
    [endpoint, debounceMs, excludeId]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    isChecking,
    validateEmail,
    clearError,
  };
};
